import { Button, Card, Description, Heading } from '@gitbook/spine-react';

import type { UpgradeAction } from '../../state';

/**
 * Compact banner rendered above the agent composer when a free, non-trial user opens an empty
 * conversation while already at the weekly message limit. It warns before a send is attempted;
 * once one is, {@link AgentLimitReachedCard} takes over inside the message flow.
 */
export function AgentLimitReachedBanner(props: {
    upgrade: UpgradeAction;
    onUpgradeClick: () => void;
}) {
    const { upgrade, onUpgradeClick } = props;

    return (
        <div className="mx-2 -mb-2 flex flex-wrap items-center gap-1 rounded-t-lg border-t border-r border-l border-upgrade-interactive bg-upgrade-surface-subtle px-3 pt-2 pb-4 text-ui-small text-base">
            <span>All free messages used for this week.</span>
            <Button
                {...upgrade}
                kind="link"
                size="xsmall"
                onPress={async () => {
                    onUpgradeClick();
                    await upgrade.onPress();
                }}
            />
        </div>
    );
}

/**
 * Card rendered inside the message flow when a free, non-trial user at the weekly limit tries to
 * send. It sits where the answer would have been, so the reason the message did not go is in the
 * conversation rather than in a toast.
 */
export function AgentLimitReachedCard(props: {
    upgrade: UpgradeAction;
    onUpgradeClick: () => void;
}) {
    const { upgrade, onUpgradeClick } = props;

    return (
        <div className="px-4 pb-4 duration-500 animate-in fade-in slide-in-from-top-2">
            <Card className="flex flex-col gap-2 p-4">
                <Heading level={3} className="text-ui-heading-medium">
                    You're out of free messages
                </Heading>
                <Description>
                    You've used all your GitBook Agent messages for this week. Upgrade to keep
                    working with GitBook Agent.
                </Description>
                <div className="mt-1 flex">
                    <Button
                        {...upgrade}
                        onPress={async () => {
                            onUpgradeClick();
                            await upgrade.onPress();
                        }}
                    />
                </div>
            </Card>
        </div>
    );
}
